import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSoftDeleteAgentFile } from "@/hooks/useAgentFiles";
import type { AgentFileNode } from "@/types/agent-skills";

/** Count all descendants (skills + sub-skills) in a node tree. */
function countDescendants(node: AgentFileNode): { skills: number; subskills: number } {
  let skills = 0;
  let subskills = 0;

  for (const child of node.children) {
    if (child.kind === "skill") {
      skills += 1;
      for (const grandchild of child.children) {
        if (grandchild.kind === "subskill") {
          subskills += 1;
        }
      }
    } else if (child.kind === "subskill") {
      subskills += 1;
    }
  }

  return { skills, subskills };
}

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: AgentFileNode | null;
  onDeleted?: () => void;
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  node,
  onDeleted,
}: DeleteConfirmDialogProps) {
  const softDelete = useSoftDeleteAgentFile();

  if (!node) return null;

  const { skills, subskills } = countDescendants(node);
  const hasCascade = skills > 0 || subskills > 0;

  const cascadeDescription = () => {
    if (!hasCascade) return null;
    const parts: string[] = [];
    if (skills > 0) parts.push(`${skills} skill${skills > 1 ? "s" : ""}`);
    if (subskills > 0) parts.push(`${subskills} sub-skill${subskills > 1 ? "s" : ""}`);
    return `This will also delete ${parts.join(" and ")}.`;
  };

  const handleDelete = async () => {
    await softDelete.mutateAsync(node.id);
    onOpenChange(false);
    onDeleted?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{node.name}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone.
            {hasCascade && (
              <>
                {" "}
                <span className="font-medium text-destructive">{cascadeDescription()}</span>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleDelete}
            disabled={softDelete.isPending}
          >
            {softDelete.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
